/**
 * Distinct upright silhouettes for the ten enemy kinds and the training
 * dummy (DESIGN 6.2 "each enemy kind = a distinct silhouette"): spider 8
 * legs, rogue small green, ranger with a bow arc, bomber carrying a bomb,
 * shaman with a blue ring, troll big brown, tree spirit green with branches,
 * skeleton white, orc grey with armor plates, jotunn huge blue.
 *
 * Visual radii/heights live here rather than in ENEMY_DEFS so the renderer
 * never depends on the data table being filled (and so a silhouette can be
 * wider than its collider for readability).
 */

import type { EnemyKind } from '@shared/index.js';
import { elementName } from '@shared/index.js';
import type { ViewEnemy } from '../state/viewModel.js';
import { ELEMENT_SWATCH, ENEMY_COLORS, hpColor, mixHex, rgba, UI } from '../palette.js';
import { circlePath, clamp01, drawGlow, drawShadow, font, hashRand, outlinedText, roundRectPath, TAU, UNIT, type Ctx } from './gfx.js';
import type { Camera } from './camera.js';
import { drawStatusAbove, drawStatusBelow, drawStatusIcons, statusTints, tintBody, type StatusLook } from './statusFx.js';
import { art, type Drawn } from './sprites.js';
import { type ClipChoice, enemyClip, enemySetName } from './spriteSets.js';
import { drawBoiler, drawBoilerGround, drawBrazier, drawBrazierGround } from './story.js';

export interface EnemyVisual { r: number; h: number; name: string; armorMax: number }

export const ENEMY_VISUAL: Readonly<Record<EnemyKind, EnemyVisual>> = {
  spider: { r: 0.35, h: 0.4, name: 'Spider', armorMax: 0 },
  rogue: { r: 0.4, h: 0.85, name: 'Rogue', armorMax: 0 },
  ranger: { r: 0.4, h: 0.9, name: 'Ranger', armorMax: 0 },
  bomber: { r: 0.4, h: 0.85, name: 'Bomber', armorMax: 0 },
  shaman: { r: 0.45, h: 1.05, name: 'Shaman', armorMax: 0 },
  troll: { r: 0.9, h: 1.5, name: 'Troll', armorMax: 0 },
  treespirit: { r: 0.8, h: 1.7, name: 'Tree Spirit', armorMax: 0 },
  skeleton: { r: 0.4, h: 0.95, name: 'Skeleton', armorMax: 0 },
  orc: { r: 0.5, h: 1.15, name: 'Orc', armorMax: 2200 },
  jotunn: { r: 1.0, h: 2.3, name: 'Jotunn', armorMax: 0 },
  dummy: { r: 0.5, h: 1.2, name: 'Dummy', armorMax: 0 },
  mire: { r: 1.0, h: 3.0, name: 'Mother Mire', armorMax: 0 },
};

const STAND_UP_S = 0.3;

/** Ground pass: shadow, status marks, the shaman's water ring, the panic marker. */
export function drawEnemyGround(ctx: Ctx, cam: Camera, e: ViewEnemy, look: StatusLook, time: number): void {
  if (e.ob === 'brazier') { drawBrazierGround(ctx, cam, e); return; }
  if (e.ob === 'boiler') { drawBoilerGround(ctx, cam, e); return; }
  if (e.k === 'mire' && (e.sub || e.str)) { drawMireWater(ctx, cam, e, time); if (e.sub) return; }
  const vis = ENEMY_VISUAL[e.k];
  const u = UNIT;
  const px = cam.sx(e.rx), py = cam.sy(e.ry);
  drawShadow(ctx, px, py, vis.r * u * 1.05, vis.r * u * 0.42, e.k === 'jotunn' ? 0.55 : 0.4);
  drawStatusBelow(ctx, px, py, vis.r * u, look, time);
  if (e.el) drawEliteRing(ctx, px, py, vis.r * u, e.el, time);
  if (e.k === 'shaman') {
    ctx.strokeStyle = rgba(ELEMENT_SWATCH.water.core, e.a === 'channel' ? 0.9 : 0.55);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(px, py, (0.75 + 0.05 * Math.sin(time * 4)) * u, 0.32 * u, 0, 0, TAU);
    ctx.stroke();
  }
}

/**
 * An elite (difficulty.ts): a gold ring under it and an inner ring in the colour of the element it is immune to,
 * turning slowly, so it reads at a glance in a crowd.
 */
function drawEliteRing(ctx: Ctx, px: number, py: number, r: number, el: NonNullable<ViewEnemy['el']>, time: number): void {
  const rx = r * 1.35, ry = rx * 0.42;
  ctx.lineWidth = 2.5;
  ctx.strokeStyle = rgba(UI.gold, 0.85);
  ctx.beginPath();
  ctx.ellipse(px, py, rx, ry, 0, 0, TAU);
  ctx.stroke();
  ctx.lineWidth = 2;
  ctx.strokeStyle = rgba(ELEMENT_SWATCH[el].core, 0.9);
  ctx.setLineDash([6, 5]);
  ctx.lineDashOffset = -time * 18;
  ctx.beginPath();
  ctx.ellipse(px, py, rx * 0.8, ry * 0.8, 0, 0, TAU);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.lineDashOffset = 0;
}

/** Body pass. */
export function drawEnemy(ctx: Ctx, cam: Camera, e: ViewEnemy, look: StatusLook, flash: number, time: number): void {
  // a story brazier rides on the dummy's body: draw the brazier, never the dummy
  if (e.ob === 'brazier') { drawBrazier(ctx, cam, e, time); return; }
  if (e.ob === 'boiler') { drawBoiler(ctx, cam, e, time); return; }
  const vis = ENEMY_VISUAL[e.k];
  const u = UNIT;
  let px = cam.sx(e.rx) + look.jx, py = cam.sy(e.ry) + look.jy;
  if (e.a === 'panic') {
    const frame = Math.floor(time * 10);
    px += (hashRand(e.id, frame) - 0.5) * 6;
    py += (hashRand(e.id + 7, frame) - 0.5) * 4;
  }
  const dir = Math.cos(e.facing) >= 0 ? 1 : -1;
  const sw = ENEMY_COLORS[e.k];
  const body = tintBody(sw.body, look, flash);
  const dark = tintBody(sw.dark, look, flash);
  const moving = e.vx !== 0 || e.vy !== 0;
  const anim = look.anim;
  const walk = moving && anim > 0 ? Math.sin(time * 12 * anim + e.id) : 0;

  // The baked body once this kind's set has landed: the clip its state asks for, status washes
  // and the hit flash over it; the shock jitter and the panic jig move it like the vector one.
  // An orc whose armour broke draws its bare set (the plated one stands in while that loads).
  const set = art.set(enemySetName(e)) ?? art.set(e.k);
  let drawn: Drawn | null = null;
  // Mother Mire under her pool: only the sinking clip (held on its last, submerged frame), or nothing at all
  const sunk = e.k === 'mire' && e.sub === 1;
  if (sunk && !set) return;
  if (set) {
    const { clip, rate } = e.k === 'mire' ? mireClip(e, look, (c) => set.has(c), time) : enemyClip(e, look, (c) => set.has(c));
    const jx = px - cam.sx(e.rx), jy = py - cam.sy(e.ry);
    ctx.save();
    if (jx !== 0 || jy !== 0) ctx.translate(jx, jy);
    drawn = set.draw(ctx, cam, e.id, clip, e.facing, time, e.rx, e.ry, { rate, tints: statusTints(look, flash) });
    ctx.restore();
  }
  if (!drawn) {
    let lying = 0;
    if (look.knockdown) lying = clamp01(look.knockdownT / STAND_UP_S);
    if (lying > 0) {
      ctx.save();
      ctx.translate(px, py);
      ctx.rotate(-dir * lying * Math.PI * 0.5);
      ctx.translate(-px, -py);
    }

    switch (e.k) {
      case 'spider': drawSpider(ctx, px, py, u, dir, body, dark, sw.accent, time, anim, moving); break;
      case 'rogue': drawRogue(ctx, px, py, u, dir, body, dark, sw.accent, walk); break;
      case 'ranger': drawRanger(ctx, px, py, u, dir, body, dark, sw.accent, walk, e.a === 'attack'); break;
      case 'bomber': drawBomber(ctx, px, py, u, dir, body, dark, time, e.a !== 'panic'); break;
      case 'shaman': drawShaman(ctx, px, py, u, dir, body, dark, time, e.a === 'channel'); break;
      case 'troll': drawTroll(ctx, px, py, u, dir, body, dark, sw.accent, walk, e.a === 'attack', time); break;
      case 'treespirit': drawTreeSpirit(ctx, px, py, u, dir, body, dark, sw.accent, time, anim); break;
      case 'skeleton': drawSkeleton(ctx, px, py, u, dir, body, dark, walk); break;
      case 'orc': drawOrc(ctx, px, py, u, dir, body, dark, sw.accent, walk, e.ar > 0); break;
      case 'jotunn': drawJotunn(ctx, px, py, u, dir, body, dark, sw.accent, walk, time); break;
      case 'dummy': drawDummy(ctx, px, py, u, body, dark, sw.accent); break;
      // stand-in until her set lands: a shaman nearly three times the size
      case 'mire': drawShaman(ctx, px, py, u * 2.6, dir, body, dark, time, e.a === 'channel'); break;
    }
    if (lying > 0) ctx.restore();
  }
  if (sunk) return;
  drawStatusAbove(ctx, px, py, vis.r * u, vis.h * u, look, time, e.facing, e.id);
  if (e.a === 'panic') {
    ctx.font = font(14, 800);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    outlinedText(ctx, '!', px, py - (vis.h + 0.35) * u + Math.sin(time * 12) * 3, ELEMENT_SWATCH.fire.core);
  }
}

/** Label pass: HP bar once damaged (armor bar for orcs), status icons, boss name. */
export function drawEnemyLabels(ctx: Ctx, cam: Camera, e: ViewEnemy): void {
  if (e.ob !== undefined || e.sub) return;
  const vis = ENEMY_VISUAL[e.k];
  const u = UNIT;
  const px = cam.sx(e.rx), py = cam.sy(e.ry);
  const damaged = e.hp < e.mhp || e.ar > 0;
  if (damaged) {
    const bw = Math.round(28 + vis.r * 36), bh = 4;
    const y = py + 0.25 * u;
    ctx.fillStyle = 'rgba(0,0,0,0.65)';
    ctx.fillRect(px - bw / 2 - 1, y - 1, bw + 2, bh + 2);
    const frac = e.mhp > 0 ? clamp01(e.hp / e.mhp) : 0;
    ctx.fillStyle = e.boss ? UI.bossHp : e.k === 'dummy' ? hpColor(frac) : UI.enemyHp;
    ctx.fillRect(px - bw / 2, y, bw * frac, bh);
    if (e.ar > 0 && vis.armorMax > 0) {
      ctx.fillStyle = rgba('#B5B9C4', 0.9);
      ctx.fillRect(px - bw / 2, y + bh + 1, bw * clamp01(e.ar / vis.armorMax), 2);
    }
  }
  drawStatusIcons(ctx, e.st, px, py + 0.62 * u, 8);
  if (e.el) {
    // what it shrugs off, in that element's colour, so the player switches combo
    ctx.font = font(9, 800);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    outlinedText(ctx, `ELITE · ${elementName(e.el).toUpperCase()}-PROOF`, px, py - (vis.h + 0.3) * u, ELEMENT_SWATCH[e.el].core);
  }
  if (e.boss || e.k === 'dummy') {
    ctx.font = font(10, 700);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    outlinedText(ctx, vis.name, px, py - (vis.h + 0.3) * u, e.boss ? UI.bossHp : UI.muted);
  }
}

// ------------------------------------------------------------------ silhouettes (feet at px,py; u = px per unit)

function eyes(ctx: Ctx, x: number, y: number, dir: number, gap: number, r: number, color: string): void {
  ctx.fillStyle = color;
  circlePath(ctx, x + dir * gap, y, r);
  ctx.fill();
  circlePath(ctx, x + dir * gap * 0.2, y, r);
  ctx.fill();
}

function drawSpider(ctx: Ctx, px: number, py: number, u: number, dir: number, body: string, dark: string, accent: string, time: number, anim: number, moving: boolean): void {
  ctx.strokeStyle = dark;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.beginPath();
  for (let i = 0; i < 4; i++) {
    const wig = moving && anim > 0 ? Math.sin(time * 16 * anim + i * 1.5) * 0.08 * u : 0;
    const sx = (i - 1.5) * 0.16 * u;
    for (const side of [-1, 1]) {
      ctx.moveTo(px + sx * 0.5, py - 0.2 * u);
      ctx.lineTo(px + sx + side * 0.42 * u, py - 0.32 * u + wig);
      ctx.lineTo(px + sx + side * 0.5 * u, py + 0.02 * u - wig);
    }
  }
  ctx.stroke();
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.ellipse(px, py - 0.18 * u, 0.3 * u, 0.2 * u, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = dark;
  circlePath(ctx, px + dir * 0.28 * u, py - 0.2 * u, 0.13 * u);
  ctx.fill();
  eyes(ctx, px + dir * 0.3 * u, py - 0.23 * u, dir, 0.08 * u, 1.5, accent);
}

function drawRogue(ctx: Ctx, px: number, py: number, u: number, dir: number, body: string, dark: string, accent: string, walk: number): void {
  ctx.fillStyle = body;
  roundRectPath(ctx, px - 0.24 * u, py - 0.6 * u, 0.48 * u, 0.6 * u, 0.1 * u);
  ctx.fill();
  ctx.fillStyle = dark;
  ctx.fillRect(px - 0.24 * u, py - 0.12 * u, 0.48 * u, 0.06 * u);
  // head with pointy ears
  ctx.fillStyle = body;
  circlePath(ctx, px, py - 0.74 * u, 0.15 * u);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(px - 0.12 * u, py - 0.78 * u); ctx.lineTo(px - 0.3 * u, py - 0.92 * u); ctx.lineTo(px - 0.08 * u, py - 0.88 * u);
  ctx.moveTo(px + 0.12 * u, py - 0.78 * u); ctx.lineTo(px + 0.3 * u, py - 0.92 * u); ctx.lineTo(px + 0.08 * u, py - 0.88 * u);
  ctx.fill();
  eyes(ctx, px + dir * 0.04 * u, py - 0.76 * u, dir, 0.07 * u, 1.3, '#FFD24A');
  // dagger
  ctx.strokeStyle = accent;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(px + dir * 0.26 * u, py - 0.35 * u + walk * 2);
  ctx.lineTo(px + dir * 0.5 * u, py - 0.5 * u + walk * 2);
  ctx.stroke();
}

function drawRanger(ctx: Ctx, px: number, py: number, u: number, dir: number, body: string, dark: string, accent: string, walk: number, attacking: boolean): void {
  const bowBehind = !attacking;
  const bow = (): void => {
    ctx.strokeStyle = accent;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(px + dir * 0.3 * u, py - 0.5 * u, 0.32 * u, -Math.PI / 2 + (dir > 0 ? 0 : Math.PI) - 0.2, Math.PI / 2 + (dir > 0 ? 0 : Math.PI) + 0.2);
    ctx.stroke();
    ctx.strokeStyle = '#DDE3EA';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(px + dir * 0.3 * u, py - 0.82 * u);
    ctx.lineTo(px + dir * 0.3 * u, py - 0.18 * u);
    ctx.stroke();
  };
  if (bowBehind) bow();
  ctx.fillStyle = body;
  roundRectPath(ctx, px - 0.22 * u, py - 0.62 * u, 0.44 * u, 0.62 * u, 0.08 * u);
  ctx.fill();
  // hood
  ctx.fillStyle = dark;
  ctx.beginPath();
  ctx.moveTo(px - 0.24 * u, py - 0.6 * u);
  ctx.lineTo(px + 0.24 * u, py - 0.6 * u);
  ctx.lineTo(px + dir * 0.05 * u, py - 0.95 * u);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#1B1C22';
  circlePath(ctx, px + dir * 0.03 * u, py - 0.7 * u, 0.1 * u);
  ctx.fill();
  eyes(ctx, px + dir * 0.05 * u, py - 0.71 * u, dir, 0.06 * u, 1.2, '#FFFFFF');
  if (!bowBehind) bow();
  void walk;
}

function drawBomber(ctx: Ctx, px: number, py: number, u: number, dir: number, body: string, dark: string, time: number, holding: boolean): void {
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.ellipse(px, py - 0.38 * u, 0.36 * u, 0.4 * u, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = dark;
  ctx.fillRect(px - 0.36 * u, py - 0.36 * u, 0.72 * u, 0.07 * u);
  circlePath(ctx, px + dir * 0.05 * u, py - 0.85 * u, 0.14 * u);
  ctx.fill();
  eyes(ctx, px + dir * 0.06 * u, py - 0.87 * u, dir, 0.07 * u, 1.3, '#FFD24A');
  if (holding) {
    // held bomb above the head, fuse sparking
    const bx = px + dir * 0.05 * u, by = py - 1.22 * u;
    ctx.strokeStyle = dark;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(px - 0.25 * u, py - 0.6 * u); ctx.lineTo(bx - 0.1 * u, by + 0.15 * u);
    ctx.moveTo(px + 0.25 * u, py - 0.6 * u); ctx.lineTo(bx + 0.1 * u, by + 0.15 * u);
    ctx.stroke();
    ctx.fillStyle = '#1B1C22';
    circlePath(ctx, bx, by, 0.2 * u);
    ctx.fill();
    ctx.globalCompositeOperation = 'lighter';
    drawGlow(ctx, ELEMENT_SWATCH.fire.core, bx + 0.12 * u, by - 0.26 * u, 14 + Math.sin(time * 20) * 4);
    ctx.globalCompositeOperation = 'source-over';
  }
}

function drawShaman(ctx: Ctx, px: number, py: number, u: number, dir: number, body: string, dark: string, time: number, channeling: boolean): void {
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.moveTo(px - 0.4 * u, py);
  ctx.lineTo(px + 0.4 * u, py);
  ctx.lineTo(px + 0.18 * u, py - 0.75 * u);
  ctx.lineTo(px - 0.18 * u, py - 0.75 * u);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = dark;
  ctx.fillRect(px - 0.4 * u, py - 0.1 * u, 0.8 * u, 0.1 * u);
  // mask
  ctx.fillStyle = '#F3EEDD';
  roundRectPath(ctx, px - 0.16 * u, py - 1.02 * u, 0.32 * u, 0.3 * u, 0.06 * u);
  ctx.fill();
  ctx.fillStyle = '#1B1C22';
  ctx.fillRect(px - 0.11 * u, py - 0.95 * u, 0.07 * u, 0.06 * u);
  ctx.fillRect(px + 0.04 * u, py - 0.95 * u, 0.07 * u, 0.06 * u);
  // feather crest
  ctx.fillStyle = ELEMENT_SWATCH.water.core;
  ctx.beginPath();
  ctx.moveTo(px, py - 1.02 * u); ctx.lineTo(px - 0.08 * u, py - 1.25 * u); ctx.lineTo(px + 0.08 * u, py - 1.25 * u);
  ctx.fill();
  // staff with water orb
  const sx = px + dir * 0.42 * u, sy = py - 0.95 * u;
  ctx.strokeStyle = '#6B4A1B';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(px + dir * 0.3 * u, py - 0.05 * u);
  ctx.lineTo(sx, sy);
  ctx.stroke();
  ctx.globalCompositeOperation = 'lighter';
  drawGlow(ctx, ELEMENT_SWATCH.water.core, sx, sy, channeling ? 34 + Math.sin(time * 12) * 6 : 18);
  ctx.globalCompositeOperation = 'source-over';
  ctx.fillStyle = ELEMENT_SWATCH.water.soft;
  circlePath(ctx, sx, sy, 0.08 * u);
  ctx.fill();
}

function drawTroll(ctx: Ctx, px: number, py: number, u: number, dir: number, body: string, dark: string, accent: string, walk: number, attacking: boolean, time: number): void {
  // knuckle-walking arms
  ctx.strokeStyle = dark;
  ctx.lineWidth = 0.22 * u;
  ctx.lineCap = 'round';
  const raise = attacking ? Math.abs(Math.sin(time * 8)) * 0.5 * u : 0;
  ctx.beginPath();
  ctx.moveTo(px - 0.5 * u, py - 0.9 * u);
  ctx.lineTo(px - 0.85 * u, py - 0.05 * u + walk * 3);
  ctx.moveTo(px + 0.5 * u, py - 0.9 * u);
  ctx.lineTo(px + 0.85 * u + dir * 0.1 * u, py - 0.05 * u - walk * 3 - raise);
  ctx.stroke();
  ctx.fillStyle = body;
  roundRectPath(ctx, px - 0.62 * u, py - 1.2 * u, 1.24 * u, 1.2 * u, 0.4 * u);
  ctx.fill();
  ctx.fillStyle = dark;
  ctx.beginPath();
  ctx.ellipse(px, py - 0.08 * u, 0.6 * u, 0.1 * u, 0, 0, TAU);
  ctx.fill();
  // head low on the shoulders
  ctx.fillStyle = body;
  circlePath(ctx, px + dir * 0.1 * u, py - 1.25 * u, 0.26 * u);
  ctx.fill();
  ctx.fillStyle = accent;
  ctx.beginPath();
  ctx.moveTo(px + dir * 0.02 * u, py - 1.12 * u); ctx.lineTo(px + dir * 0.06 * u, py - 1.3 * u); ctx.lineTo(px + dir * 0.12 * u, py - 1.12 * u);
  ctx.moveTo(px + dir * 0.22 * u, py - 1.12 * u); ctx.lineTo(px + dir * 0.28 * u, py - 1.3 * u); ctx.lineTo(px + dir * 0.34 * u, py - 1.12 * u);
  ctx.fill();
  eyes(ctx, px + dir * 0.14 * u, py - 1.32 * u, dir, 0.1 * u, 2, '#FFD24A');
}

function drawTreeSpirit(ctx: Ctx, px: number, py: number, u: number, dir: number, body: string, dark: string, accent: string, time: number, anim: number): void {
  const sway = anim > 0 ? Math.sin(time * 1.5 * anim) * 0.06 * u : 0;
  // trunk
  ctx.fillStyle = dark;
  ctx.beginPath();
  ctx.moveTo(px - 0.45 * u, py);
  ctx.lineTo(px + 0.45 * u, py);
  ctx.lineTo(px + 0.2 * u + sway, py - 1.1 * u);
  ctx.lineTo(px - 0.2 * u + sway, py - 1.1 * u);
  ctx.closePath();
  ctx.fill();
  // branches
  ctx.strokeStyle = dark;
  ctx.lineWidth = 0.08 * u;
  ctx.lineCap = 'round';
  ctx.beginPath();
  const top = py - 1.1 * u;
  ctx.moveTo(px + sway, top); ctx.lineTo(px - 0.55 * u + sway * 2, top - 0.45 * u);
  ctx.moveTo(px + sway, top); ctx.lineTo(px + 0.5 * u + sway * 2, top - 0.5 * u);
  ctx.moveTo(px + sway, top); ctx.lineTo(px + 0.05 * u + sway * 2, top - 0.6 * u);
  ctx.moveTo(px - 0.2 * u, py - 0.6 * u); ctx.lineTo(px - 0.7 * u, py - 0.9 * u);
  ctx.moveTo(px + 0.2 * u, py - 0.55 * u); ctx.lineTo(px + 0.7 * u, py - 0.8 * u);
  ctx.stroke();
  // leaf blobs
  ctx.fillStyle = body;
  circlePath(ctx, px - 0.55 * u + sway * 2, top - 0.5 * u, 0.22 * u); ctx.fill();
  circlePath(ctx, px + 0.5 * u + sway * 2, top - 0.55 * u, 0.24 * u); ctx.fill();
  circlePath(ctx, px + 0.05 * u + sway * 2, top - 0.65 * u, 0.26 * u); ctx.fill();
  ctx.fillStyle = accent;
  circlePath(ctx, px + 0.1 * u + sway * 2, top - 0.7 * u, 0.12 * u); ctx.fill();
  // glowing eyes in the trunk
  ctx.globalCompositeOperation = 'lighter';
  eyes(ctx, px + dir * 0.08 * u, py - 0.8 * u, dir, 0.12 * u, 2.5, accent);
  ctx.globalCompositeOperation = 'source-over';
}

function drawSkeleton(ctx: Ctx, px: number, py: number, u: number, dir: number, body: string, dark: string, walk: number): void {
  ctx.strokeStyle = body;
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  ctx.beginPath();
  // legs
  ctx.moveTo(px - 0.1 * u, py - 0.4 * u); ctx.lineTo(px - 0.16 * u + walk * 2, py);
  ctx.moveTo(px + 0.1 * u, py - 0.4 * u); ctx.lineTo(px + 0.16 * u - walk * 2, py);
  // spine
  ctx.moveTo(px, py - 0.4 * u); ctx.lineTo(px, py - 0.78 * u);
  // arms
  ctx.moveTo(px - 0.05 * u, py - 0.7 * u); ctx.lineTo(px - 0.3 * u, py - 0.35 * u - walk * 2);
  ctx.moveTo(px + 0.05 * u, py - 0.7 * u); ctx.lineTo(px + dir * 0.35 * u, py - 0.45 * u + walk * 2);
  // ribs
  for (let i = 0; i < 3; i++) {
    const y = py - 0.7 * u + i * 0.1 * u;
    ctx.moveTo(px - 0.18 * u, y); ctx.lineTo(px + 0.18 * u, y);
  }
  ctx.stroke();
  ctx.fillStyle = body;
  ctx.fillRect(px - 0.14 * u, py - 0.45 * u, 0.28 * u, 0.08 * u);
  // skull
  circlePath(ctx, px, py - 0.92 * u, 0.15 * u);
  ctx.fill();
  ctx.fillRect(px - 0.08 * u, py - 0.84 * u, 0.16 * u, 0.08 * u);
  ctx.fillStyle = dark;
  ctx.fillRect(px - 0.09 * u, py - 0.96 * u, 0.06 * u, 0.05 * u);
  ctx.fillRect(px + 0.03 * u, py - 0.96 * u, 0.06 * u, 0.05 * u);
}

function drawOrc(ctx: Ctx, px: number, py: number, u: number, dir: number, body: string, dark: string, accent: string, walk: number, armored: boolean): void {
  ctx.fillStyle = body;
  roundRectPath(ctx, px - 0.38 * u, py - 0.85 * u, 0.76 * u, 0.85 * u, 0.15 * u);
  ctx.fill();
  ctx.fillStyle = dark;
  ctx.fillRect(px - 0.38 * u, py - 0.12 * u, 0.76 * u, 0.1 * u);
  // arms
  ctx.strokeStyle = body;
  ctx.lineWidth = 0.16 * u;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(px - 0.38 * u, py - 0.7 * u); ctx.lineTo(px - 0.55 * u, py - 0.3 * u + walk * 2);
  ctx.moveTo(px + 0.38 * u, py - 0.7 * u); ctx.lineTo(px + 0.55 * u, py - 0.3 * u - walk * 2);
  ctx.stroke();
  if (armored) {
    // plates: chest, belly, shoulders (they "fall off" as chunks when armor breaks)
    ctx.fillStyle = accent;
    roundRectPath(ctx, px - 0.3 * u, py - 0.8 * u, 0.6 * u, 0.3 * u, 0.05 * u); ctx.fill();
    roundRectPath(ctx, px - 0.26 * u, py - 0.46 * u, 0.52 * u, 0.26 * u, 0.05 * u); ctx.fill();
    roundRectPath(ctx, px - 0.55 * u, py - 0.9 * u, 0.28 * u, 0.2 * u, 0.05 * u); ctx.fill();
    roundRectPath(ctx, px + 0.27 * u, py - 0.9 * u, 0.28 * u, 0.2 * u, 0.05 * u); ctx.fill();
    ctx.strokeStyle = dark;
    ctx.lineWidth = 1;
    roundRectPath(ctx, px - 0.3 * u, py - 0.8 * u, 0.6 * u, 0.3 * u, 0.05 * u); ctx.stroke();
  }
  // head + tusks
  ctx.fillStyle = body;
  circlePath(ctx, px + dir * 0.04 * u, py - 1.0 * u, 0.18 * u);
  ctx.fill();
  ctx.fillStyle = '#F3EEDD';
  ctx.beginPath();
  ctx.moveTo(px + dir * 0.08 * u, py - 0.9 * u); ctx.lineTo(px + dir * 0.12 * u, py - 1.02 * u); ctx.lineTo(px + dir * 0.16 * u, py - 0.9 * u);
  ctx.moveTo(px - dir * 0.04 * u, py - 0.9 * u); ctx.lineTo(px, py - 1.02 * u); ctx.lineTo(px + dir * 0.04 * u, py - 0.9 * u);
  ctx.fill();
  eyes(ctx, px + dir * 0.08 * u, py - 1.04 * u, dir, 0.08 * u, 1.5, '#FF3B3B');
}

function drawJotunn(ctx: Ctx, px: number, py: number, u: number, dir: number, body: string, dark: string, accent: string, walk: number, time: number): void {
  // legs
  ctx.fillStyle = dark;
  roundRectPath(ctx, px - 0.6 * u, py - 0.9 * u, 0.45 * u, 0.9 * u + walk * 3, 0.1 * u); ctx.fill();
  roundRectPath(ctx, px + 0.15 * u, py - 0.9 * u, 0.45 * u, 0.9 * u - walk * 3, 0.1 * u); ctx.fill();
  // torso
  ctx.fillStyle = body;
  roundRectPath(ctx, px - 0.85 * u, py - 2.0 * u, 1.7 * u, 1.25 * u, 0.3 * u);
  ctx.fill();
  // arms
  ctx.strokeStyle = body;
  ctx.lineWidth = 0.3 * u;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(px - 0.8 * u, py - 1.8 * u); ctx.lineTo(px - 1.05 * u, py - 0.8 * u + walk * 4);
  ctx.moveTo(px + 0.8 * u, py - 1.8 * u); ctx.lineTo(px + 1.05 * u + dir * 0.1 * u, py - 0.8 * u - walk * 4);
  ctx.stroke();
  // frost crust on the shoulders
  ctx.fillStyle = rgba(accent, 0.75);
  roundRectPath(ctx, px - 0.9 * u, py - 2.05 * u, 1.8 * u, 0.22 * u, 0.1 * u);
  ctx.fill();
  // head with horns and icy beard
  ctx.fillStyle = body;
  circlePath(ctx, px + dir * 0.05 * u, py - 2.25 * u, 0.3 * u);
  ctx.fill();
  ctx.strokeStyle = '#F3EEDD';
  ctx.lineWidth = 0.08 * u;
  ctx.beginPath();
  ctx.moveTo(px - 0.25 * u, py - 2.4 * u); ctx.quadraticCurveTo(px - 0.5 * u, py - 2.6 * u, px - 0.4 * u, py - 2.85 * u);
  ctx.moveTo(px + 0.25 * u, py - 2.4 * u); ctx.quadraticCurveTo(px + 0.5 * u, py - 2.6 * u, px + 0.4 * u, py - 2.85 * u);
  ctx.stroke();
  ctx.fillStyle = mixHex(accent, '#FFFFFF', 0.5);
  ctx.beginPath();
  ctx.moveTo(px - 0.2 * u, py - 2.1 * u); ctx.lineTo(px + 0.2 * u, py - 2.1 * u); ctx.lineTo(px + dir * 0.05 * u, py - 1.7 * u);
  ctx.closePath();
  ctx.fill();
  ctx.globalCompositeOperation = 'lighter';
  eyes(ctx, px + dir * 0.12 * u, py - 2.3 * u, dir, 0.14 * u, 2.5 + Math.sin(time * 5), accent);
  ctx.globalCompositeOperation = 'source-over';
}

function drawDummy(ctx: Ctx, px: number, py: number, u: number, body: string, dark: string, accent: string): void {
  // cross base + post
  ctx.fillStyle = dark;
  ctx.beginPath();
  ctx.ellipse(px, py, 0.4 * u, 0.14 * u, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = body;
  ctx.fillRect(px - 0.07 * u, py - 1.0 * u, 0.14 * u, 1.0 * u);
  ctx.fillRect(px - 0.4 * u, py - 0.78 * u, 0.8 * u, 0.08 * u);
  // straw target
  ctx.fillStyle = accent;
  circlePath(ctx, px, py - 1.0 * u, 0.32 * u);
  ctx.fill();
  ctx.fillStyle = UI.hpRed;
  circlePath(ctx, px, py - 1.0 * u, 0.2 * u);
  ctx.fill();
  ctx.fillStyle = accent;
  circlePath(ctx, px, py - 1.0 * u, 0.1 * u);
  ctx.fill();
  ctx.strokeStyle = dark;
  ctx.lineWidth = 1.5;
  circlePath(ctx, px, py - 1.0 * u, 0.32 * u);
  ctx.stroke();
}

// ------------------------------------------------------------------ Mother Mire (story act 1's boss)

/** Seconds her rising clip plays after she comes up out of her pool (the set's `rise`). */
const MIRE_RISE_S = 1.1;
/** When each Mire last came up (by id), so the rise plays once. */
const mireRose = new Map<number, { sub: boolean; at: number }>();

/**
 * Her clip: sinking while under the pool (one-shot, held on the submerged
 * frame), rising for a moment after, the slam while she winds one up (her
 * `attack` state), else what any enemy's state asks for.
 */
function mireClip(e: ViewEnemy, look: StatusLook, has: (clip: string) => boolean, time: number): ClipChoice {
  const prev = mireRose.get(e.id);
  const sub = e.sub === 1;
  if (!prev || prev.sub !== sub) mireRose.set(e.id, { sub, at: prev ? time : -Infinity });
  if (sub && has('sink')) return { clip: 'sink', rate: 1 };
  const since = time - (mireRose.get(e.id)?.at ?? -Infinity);
  if (!sub && since < MIRE_RISE_S && has('rise')) return { clip: 'rise', rate: 1 };
  if (e.a === 'attack' && has('slam')) return { clip: 'slam', rate: 1 };
  return enemyClip(e, look, has);
}

/** Her pool while she hides in it (rings of ripples), or the ice she is stranded on (a pale ring). */
function drawMireWater(ctx: Ctx, cam: Camera, e: ViewEnemy, time: number): void {
  const px = cam.sx(e.rx), py = cam.sy(e.ry), u = UNIT;
  if (e.str) {
    ctx.strokeStyle = 'rgba(210,240,255,0.8)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(px, py, 1.5 * u, 0.6 * u, 0, 0, TAU);
    ctx.stroke();
    return;
  }
  ctx.strokeStyle = rgba(ELEMENT_SWATCH.water.core, 0.6);
  ctx.lineWidth = 2;
  for (let i = 0; i < 3; i++) {
    const f = (time * 0.5 + i / 3) % 1;
    ctx.globalAlpha = 1 - f;
    ctx.beginPath();
    ctx.ellipse(px, py, (0.4 + f * 2.2) * u, (0.15 + f * 0.9) * u, 0, 0, TAU);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}
